import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RouteRedirect } from '@/routing/RouteRedirect';
import { AlertCircle, CloudOff, ShieldAlert, WifiOff } from 'lucide-react';
import { Button, buttonClasses } from '@/components/Button';
import { AUTH_CARD_CLASS, AuthFrame } from '@/components/AuthFrame';
import { FeatItWordmark } from '@/components/FeatItWordmark';
import { signedOutQuietLinkClasses } from '@/features/auth/signedOutChrome';
import { LegalLink } from '@/features/legal/LegalLink';
import { LegalLinks } from '@/features/legal/LegalLinks';
import {
  DEPLOYMENT_IDENTITY_FAILURES,
  deploymentIconUrl,
  readDeploymentIdentity,
  type DeploymentIdentityAnswerType,
  type DeploymentIdentityFailureType,
} from './deploymentIdentity';
import {
  IDENTITY_FAILURE_DETAILS,
  IDENTITY_FAILURE_TITLES,
  currentFirstScreenTarget,
  firstScreenSignUp,
} from './firstScreen';
import { useT } from '@/i18n/LocaleProvider';
import { registerEnglishArea } from '@/i18n/catalogue';
import { busyControlProps } from '@/utils/busyControl';
import { onboarding } from '@featit/shared/i18n/messages/en/onboarding';

registerEnglishArea('onboarding', onboarding);

export interface IWelcomePageProps {
  /** Injected in tests; the real screen asks the deployment, natively on the desktop. */
  readIdentity?: typeof readDeploymentIdentity;
}

const FAILURE_ICONS: Record<DeploymentIdentityFailureType, typeof AlertCircle> = {
  [DEPLOYMENT_IDENTITY_FAILURES.offline]: WifiOff,
  [DEPLOYMENT_IDENTITY_FAILURES.unreachable]: CloudOff,
  [DEPLOYMENT_IDENTITY_FAILURES.notFeatit]: AlertCircle,
  [DEPLOYMENT_IDENTITY_FAILURES.certificate]: ShieldAlert,
};

/**
 * The desktop app's first screen (F-0518, D-0221 (a)): Create account and Sign in, for the cloud
 * unless app data chose another deployment, and nothing to type.
 *
 * The iPhone app opens the same way (F-0333). An address is one quiet link away, on the secondary
 * screen (`/connect`), because most people never need one — and the few who do are told there whose
 * deployment they reached before anything is remembered.
 */
export function WelcomePage({ readIdentity = readDeploymentIdentity }: IWelcomePageProps = {}) {
  const t = useT();
  const navigate = useNavigate();
  const [target] = useState(currentFirstScreenTarget);
  const [answer, setAnswer] = useState<DeploymentIdentityAnswerType | null>(null);
  const [checking, setChecking] = useState(false);

  const check = useCallback(async () => {
    if (!target) return;
    setChecking(true);
    const next = await readIdentity(target.address);
    setAnswer(next);
    setChecking(false);
  }, [readIdentity, target]);

  useEffect(() => {
    void check();
  }, [check]);

  // An install with no deployment at all — no build address and none chosen — has only one way on.
  if (!target) return <RouteRedirect to="/connect" />;

  const identity = answer?.outcome === 'verified' ? answer.identity : null;
  const failure = answer && answer.outcome !== 'verified' ? answer.outcome : null;
  const signUp = firstScreenSignUp(identity, t);
  const FailureIcon = failure ? FAILURE_ICONS[failure] : null;
  const icon = identity ? deploymentIconUrl(identity, target.address) : null;

  return (
    <AuthFrame
      firstScreen
      title={
        <FeatItWordmark className="mx-auto h-[22px] w-auto text-gray-900 dark:text-gray-100" />
      }
      subtitle={t('onboarding.welcome.subtitle')}
    >
      {/*
       * A deployment app data chose is named — by what it calls itself, never by its address. The
       * cloud is not: it is what this app is, and naming it would read as a choice somebody made.
       */}
      {!target.isCloud && !failure && (
        <div
          data-testid="welcome-deployment"
          className="mb-4 flex min-h-touch items-center justify-center gap-2.5 text-[13px] text-gray-600 dark:text-gray-300"
        >
          {icon && <img src={icon} alt="" className="h-6 w-6 shrink-0 rounded-md object-contain" />}
          <span className="min-w-0 truncate">
            {identity ? (
              <>
                {t.rich('onboarding.welcome.forName', {
                  name: identity.name,
                  identity: (chunks: ReactNode) => (
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{chunks}</span>
                  ),
                })}
              </>
            ) : (
              <span className="invisible">&nbsp;</span>
            )}
          </span>
        </div>
      )}

      <div className={AUTH_CARD_CLASS}>
        {failure && FailureIcon && (
          <div
            role="alert"
            data-testid="welcome-failure"
            className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 dark:border-amber-500/30 dark:bg-amber-500/10"
          >
            <div className="flex items-start gap-2.5">
              <FailureIcon
                aria-hidden="true"
                className="mt-px h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300"
              />
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-amber-900 dark:text-amber-200">
                  {t(IDENTITY_FAILURE_TITLES[failure])}
                </p>
                <p className="mt-1 text-[13px] leading-5 text-amber-800 dark:text-amber-200/90">
                  {t(IDENTITY_FAILURE_DETAILS[failure])}
                </p>
                <button
                  type="button"
                  onClick={() => void check()}
                  {...busyControlProps(checking)}
                  className="mt-2 rounded text-[13px] font-medium text-amber-900 evx-banner-action transition-colors hover:text-amber-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/40 disabled:opacity-60 data-[busy]:opacity-60 dark:text-amber-200 dark:hover:text-amber-100"
                >
                  {checking ? t('onboarding.welcome.tryingAgain') : t('onboarding.common.tryAgain')}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {signUp.offersCreateAccount && (
            <Button
              type="button"
              size="lg"
              className="w-full"
              onClick={() => navigate('/register')}
            >
              {t('onboarding.welcome.createAccount')}
            </Button>
          )}
          {/* T-0615, AC-W19c: closed, the early-access form takes Create account's place. */}
          {signUp.earlyAccessUrl && (
            <LegalLink
              href={signUp.earlyAccessUrl}
              className={buttonClasses({ size: 'lg', className: 'w-full' })}
            >
              {t('onboarding.welcome.earlyAccess')}
            </LegalLink>
          )}
          <Button
            type="button"
            size="lg"
            variant={signUp.offersCreateAccount || signUp.earlyAccessUrl ? 'secondary' : 'primary'}
            className="w-full"
            onClick={() => navigate('/login')}
          >
            {t('onboarding.welcome.signIn')}
          </Button>
        </div>

        {signUp.note && (
          <p
            data-testid="welcome-sign-up-note"
            className="mt-4 text-center text-[13px] leading-5 text-gray-500 dark:text-gray-400"
          >
            {signUp.note}
          </p>
        )}
      </div>

      <p className="mt-6 text-center">
        <Link to="/connect" className={signedOutQuietLinkClasses}>
          {t('onboarding.welcome.otherServer')}
        </Link>
      </p>
      {/* F-0554, AC-TA9: the desktop's first screen links Imprint, Privacy and Terms too. */}
      <LegalLinks />
    </AuthFrame>
  );
}
